import React, { useState } from "react";
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

export const Export = ({ data, className = "" }) => {
  const [showOptions, setShowOptions] = useState(false);

  // Get current date and time in IST
  const getCurrentDateTimeIST = () => {
    const now = new Date();
    const offset = 5.5 * 60; // IST offset in minutes
    const localTime = now.getTime();
    const localOffset = now.getTimezoneOffset() * 60000; // Local offset in milliseconds
    const utc = localTime + localOffset; // UTC time in milliseconds
    const ist = utc + (offset * 60000); // IST time in milliseconds
    const istDate = new Date(ist);
    return istDate.toISOString().slice(0, 19).replace('T', ' ');
  };

  // Get current date for filename in IST
  const getFormattedDateIST = () => {
    const now = new Date();
    const offset = 5.5 * 60; // IST offset in minutes
    const localTime = now.getTime();
    const localOffset = now.getTimezoneOffset() * 60000; // Local offset in milliseconds
    const utc = localTime + localOffset; // UTC time in milliseconds
    const ist = utc + (offset * 60000); // IST time in milliseconds
    const istDate = new Date(ist);
    return istDate.toISOString().slice(0, 10);
  };

  // Get current user (replace with actual user fetching logic if needed)
  const getCurrentUser = () => {
    return "aagneye-syam";
  };

  // Export as PDF with standardized look
  const exportPDF = () => {
    try {
      // Create new PDF document in portrait orientation
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Add header section
      doc.setFillColor(41, 128, 185); // Header background color
      doc.rect(0, 0, 210, 30, 'F');

      // Add white text for header
      doc.setTextColor(255, 255, 255);
      doc.setFont(undefined, 'bold');
      doc.setFontSize(20);
      doc.text('IMS Confident Group', 15, 15);
      doc.setFontSize(16);
      doc.text('Location Management Report', 15, 25);

      // Add metadata section
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.text(`Generated on: ${getCurrentDateTimeIST()} (IST)`, 15, 40);
      doc.text(`Generated by: ${getCurrentUser()}`, 15, 45);

      // Add horizontal line
      doc.setDrawColor(41, 128, 185);
      doc.setLineWidth(0.5);
      doc.line(15, 50, 195, 50);

      // Define columns for the table
      const columns = [
        { header: 'Location ID', dataKey: 'LocationID', width: 30 },
        { header: 'Location Name', dataKey: 'LocationName', width: 60 },
        { header: 'Location Head Email', dataKey: 'LocationEmail', width: 80 }
      ];

      // Prepare data
      const tableData = data.map(item => ({
        LocationID: item.LocationID || '',
        LocationName: item.LocationName || '',
        LocationEmail: item.LocationEmail || ''
      }));

      // Add table with improved styling
      doc.autoTable({
        columns: columns,
        body: tableData,
        startY: 55,
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontSize: 12,
          fontStyle: 'bold',
          halign: 'left',
          cellPadding: 3
        },
        bodyStyles: {
          fontSize: 11,
          halign: 'left',
          cellPadding: 3,
          lineWidth: 0.1
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        margin: { top: 65, left: 15, right: 15, bottom: 25 },
        tableWidth: 'auto',
        styles: {
          overflow: 'linebreak',
          cellWidth: 'wrap'
        },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 60 },
          2: { cellWidth: 80 }
        },
        didDrawPage: function(data) {
          doc.setFontSize(10);
          doc.setTextColor(128, 128, 128);
          const pageSize = doc.internal.pageSize;
          const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
          const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
          
          // Add page number on the left
          doc.text(
            `Page ${data.pageNumber} of ${doc.getNumberOfPages()}`,
            data.settings.margin.left,
            pageHeight - 10
          );

          // Add centered footer with current date and time in IST
          const footer = `© Confident Indent Management - ${getCurrentDateTimeIST()}`;
          const footerWidth = doc.getStringUnitWidth(footer) * doc.internal.getFontSize() / doc.internal.scaleFactor;
          const footerX = (pageWidth - footerWidth) / 2;
          doc.text(footer, footerX, pageHeight - 10);

          // Add header on each page after first page
          if (data.pageNumber > 1) {
            doc.setFillColor(41, 128, 185);
            doc.rect(0, 0, 210, 20, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(16);
            doc.text('Location Management Report', 15, 15);
          }
        }
      });

      // Save the PDF
      doc.save(`location_report_${getFormattedDateIST()}.pdf`);
      setShowOptions(false);
    } catch (error) {
      console.error("Error exporting PDF:", error);
      alert("Failed to export PDF");
    }
  };

  // Export as Excel with metadata
  const exportExcel = () => {
    try {
      // Create workbook
      const wb = XLSX.utils.book_new();

      // Prepare data
      const formattedData = data.map(item => ({
        'Location ID': item.LocationID || '',
        'Location Name': item.LocationName || '',
        'Location Head Email': item.LocationEmail || ''
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(formattedData);

      // Set column widths
      ws['!cols'] = [
        { wch: 15 }, // Location ID
        { wch: 30 }, // Location Name
        { wch: 35 }  // Location Head Email
      ];

      // Style the headers
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_cell({ r: 0, c: C });
        if (!ws[address]) continue;
        ws[address].s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { patternType: "solid", fgColor: { rgb: "2980B9" } },
          alignment: { horizontal: "center", vertical: "center" }
        };
      }

      // Add data worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Locations");

      // Create metadata sheet
      const metadata = [
        ['IMS Confident Group'],
        ['Location Management Report'],
        [`Generated on: ${getCurrentDateTimeIST()} (IST)`],
        [`Generated by: ${getCurrentUser()}`],
        [], // Empty line for spacing
      ];
      const wsMeta = XLSX.utils.aoa_to_sheet(metadata);
      XLSX.utils.book_append_sheet(wb, wsMeta, "Metadata");

      // Save the file
      XLSX.writeFile(wb, `location_report_${getFormattedDateIST()}.xlsx`);
      setShowOptions(false);
    } catch (error) {
      console.error("Error exporting Excel:", error);
      alert("Failed to export Excel");
    }
  };

  // Export as CSV with metadata
  const exportCSV = () => {
    try {
      // Prepare data rows with headers
      const headers = ['Location ID', 'Location Name', 'Location Head Email'];
      const dataRows = data.map(row => [
        row.LocationID || '',
        row.LocationName || '',
        row.LocationEmail || ''
      ]);

      // Create CSV content with metadata
      const metadata = [
        ['IMS Confident Group'],
        ['Location Management Report'],
        [`Generated on: ${getCurrentDateTimeIST()} (IST)`],
        [`Generated by: ${getCurrentUser()}`],
        [], // Empty line for spacing
        headers
      ];

      // Combine metadata and data
      const csvContent = [
        ...metadata,
        ...dataRows
      ].map(row => row.map(cell => 
        typeof cell === 'string' && (cell.includes(',') || cell.includes('"') || cell.includes('\n'))
          ? `"${cell.replace(/"/g, '""')}"` 
          : cell
      ).join(',')).join('\n');

      // Create and download file
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const fileName = `location_report_${getFormattedDateIST()}.csv`;

      if (navigator.msSaveBlob) {
        navigator.msSaveBlob(blob, fileName);
      } else {
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
      setShowOptions(false);
    } catch (error) {
      console.error("Error exporting CSV:", error);
      alert("Failed to export CSV");
    }
  };

  // Click outside handler
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOptions && !event.target.closest('.export-container')) {
        setShowOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOptions]);

  return (
    <div className="relative export-container">
      <button
        onClick={() => setShowOptions(!showOptions)}
        className={`px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors duration-200 flex items-center gap-2 ${className}`}
      >
        <span>Export</span>
      </button>

      {showOptions && (
        <div className="absolute z-50 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
          <div className="py-1" role="menu" aria-orientation="vertical">
            <button
              onClick={exportPDF}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center gap-2"
              role="menuitem"
            >
              <span>Export as PDF</span>
            </button>
            <button
              onClick={exportCSV}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center gap-2"
              role="menuitem"
            >
              <span>Export as CSV</span>
            </button>
            <button
              onClick={exportExcel}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center gap-2"
              role="menuitem"
            >
              <span>Export as Excel</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};